import _ from 'underscore';
import { YahooDataDisplayHints } from './constants';

/**
 * Permissions object defining some features permissions as follows:
 * @typedef {object} Permissions
 * @property {bool} CrossPartnerAgency - true of the customer is opted-in this pilot
 */

/**
 * Create a model for yahoo-customer based on input parameters.
 * @param {bool} isCustomerManagedByYahoo - true if the customer is managed by Y!
 * @param {bool} defaultMSView - true to display microsoft view even if the customer
 *  is managed by yahoo, if permissions. More generally comes from query parameters.
 * @param {bool} isInternalUser - true if the user is internal user
 * @param {Permissions} permissions - object for permissions and features
 * @param {bool} allowYahooCustomerAccessMSView - true to force allowing the user
 *  to display microsoft view for customers managed by yahoo even if permissions are not granted
 * @returns {null} - converted value with appropriate type
 */
export const getYahooModel = ({
  isCustomerManagedByYahoo,
  defaultMSView,
  isInternalUser,
  permissions = {},
  allowYahooCustomerAccessMSView,
} = {}) => {
  const { CrossPartnerAgency } = permissions;
  const isManagedByYahoo = !!isCustomerManagedByYahoo;
  const isCrossPartnerAgency = !!CrossPartnerAgency;

  const canUserAccessCrossPartnerData =
    (isManagedByYahoo && (isInternalUser || allowYahooCustomerAccessMSView === true)) ||
    isCrossPartnerAgency;

  const canUserAccessMicrosoftData = !isManagedByYahoo || canUserAccessCrossPartnerData;

  const canUserAccessYahooData = isManagedByYahoo || canUserAccessCrossPartnerData;

  const canUserSwitchCrossPartnerData =
    (isManagedByYahoo && isInternalUser) || isCrossPartnerAgency;

  const getCurrentYahooDisplayHints = () => {
    if (canUserAccessCrossPartnerData) {
      if (_.isBoolean(defaultMSView)) {
        return defaultMSView ? YahooDataDisplayHints.Optional : YahooDataDisplayHints.Visible;
      }
      // By default, CrossPartnerAgency should see MSView and others should see YahooView
      return isCrossPartnerAgency ? YahooDataDisplayHints.Optional : YahooDataDisplayHints.Visible;
    } else if (isManagedByYahoo) {
      return YahooDataDisplayHints.Visible;
    }
    return YahooDataDisplayHints.NotAvailable;
  };
  const currentYahooDisplayHints = getCurrentYahooDisplayHints();

  const showYahooView = currentYahooDisplayHints === YahooDataDisplayHints.Visible;

  return {
    isManagedByYahoo,
    canUserAccessCrossPartnerData,
    canUserAccessMicrosoftData,
    canUserAccessYahooData,
    canUserSwitchCrossPartnerData,
    currentYahooDisplayHints,
    showYahooView,
  };
};
